import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  HANDOFF_DOMAINS, handoffError, handoffSnapshotState, handoffSummary,
  type HandoffDomain, type HandoffOwner,
} from '../lib/missionControlHandoff';

interface Props {
  linkId: string;
  linkVersion: number;
  locationId: string;
  staffEnabled: boolean;
  linkStatus: string;
}

interface Review {
  domain: HandoffDomain;
  owner: HandoffOwner;
  version: number;
  linkVersion: number;
  hash: string | null;
  observedAt: string | null;
}

function dateLabel(value: string | null | undefined) {
  if (!value || !Number.isFinite(Date.parse(value))) return 'Not yet received';
  return new Date(value).toLocaleString();
}

export function MissionControlDataHandoff({ linkId, linkVersion, locationId, staffEnabled, linkStatus }: Props) {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const heading = useId();
  const [review, setReview] = useState<Review | null>(null);
  const [reason, setReason] = useState('');
  const [compared, setCompared] = useState(false);
  const [now, setNow] = useState(Date.now());
  const canManage = role === 'admin' || role === 'superadmin';
  const queryKey = ['mission-control-data-handoff', user?.id, linkId, linkVersion];
  const read = async () => {
    const [staging, ownership] = await Promise.all([
      supabase.from('mission_control_compatibility_staging')
        .select('link_id, domain, link_version, provider_id, observed_at, snapshot_hash, payload')
        .eq('link_id', linkId),
      supabase.from('mission_control_compatibility_ownership')
        .select('link_id, domain, owner, version, last_accepted_observed_at, reviewed_link_version, last_reason')
        .eq('link_id', linkId),
    ]);
    if (staging.error) throw staging.error;
    if (ownership.error) throw ownership.error;
    return { staging: staging.data, ownership: ownership.data };
  };
  const records = useQuery({
    queryKey, queryFn: read, enabled: Boolean(user?.id && role && linkId),
    refetchInterval: 30_000,
  });
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    setReview(null); setReason(''); setCompared(false);
  }, [linkId, linkVersion, user?.id, role]);

  const save = useMutation({
    mutationFn: async () => {
      if (!canManage || !review || review.linkVersion !== linkVersion || reason.trim().length < 5) {
        throw { code: '40001' };
      }
      // Re-read before the checked RPC so background changes cannot reuse the visible review.
      const current = await read();
      const owner = current.ownership.find((row) => row.domain === review.domain);
      const stage = current.staging.find((row) => row.domain === review.domain);
      if ((owner?.version ?? 0) !== review.version || (review.owner === 'mission_control'
        && (!compared || !staffEnabled || linkStatus !== 'verified'
          || stage?.snapshot_hash !== review.hash || stage?.observed_at !== review.observedAt
          || !handoffSnapshotState(stage, linkVersion).ready))) throw { code: '40001' };
      const { error } = await supabase.rpc('set_mission_control_compatibility_owner', {
        p_link_id: linkId, p_expected_link_version: review.linkVersion,
        p_domain: review.domain, p_expected_version: review.version,
        p_owner: review.owner, p_reason: reason.trim(),
        p_expected_snapshot_hash: review.owner === 'mission_control' ? review.hash ?? undefined : undefined,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setReview(null); setReason(''); setCompared(false);
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ['mission-control-site-links'] });
      await queryClient.invalidateQueries({ queryKey: ['locations', locationId] });
    },
    onError: async () => { setCompared(false); await records.refetch(); },
  });

  const openReview = (domain: HandoffDomain, owner: HandoffOwner) => {
    const current = records.data?.ownership.find((row) => row.domain === domain);
    const stage = records.data?.staging.find((row) => row.domain === domain);
    setReview({ domain, owner, version: current?.version ?? 0, linkVersion,
      hash: stage?.snapshot_hash ?? null, observedAt: stage?.observed_at ?? null });
    setReason(''); setCompared(false); save.reset();
  };
  const currentOwner = records.data?.ownership.find((row) => row.domain === review?.domain);
  const currentStage = records.data?.staging.find((row) => row.domain === review?.domain);
  const reviewChanged = Boolean(review && (review.linkVersion !== linkVersion
    || review.version !== (currentOwner?.version ?? 0)
    || (review.owner === 'mission_control' && (review.hash !== currentStage?.snapshot_hash
      || review.observedAt !== currentStage?.observed_at))));
  const sourceReady = staffEnabled && linkStatus === 'verified'
    && handoffSnapshotState(currentStage, linkVersion, now).ready;

  return <section aria-labelledby={heading} className="rounded border border-border bg-surface-2 p-3 space-y-3">
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={heading} className="text-sm font-semibold text-text">Data source handoff</h3>
        <button type="button" disabled={records.isFetching || save.isPending} onClick={() => void records.refetch()} className="text-xs text-primary disabled:opacity-40">Refresh snapshots</button>
      </div>
      <p className="mt-1 text-xs text-text-muted">Portal remains the data owner until an administrator reviews and transfers each provider domain. A handoff updates existing Portal reports and customer network pages, including device/client names, IP and MAC addresses, WLANs and alerts. Hiding the monitoring summary does not hide these existing network records.</p>
    </div>
    {records.isLoading && <p role="status" className="text-xs text-text-muted">Loading data owners…</p>}
    {records.isError && <p role="alert" className="text-xs text-error">Data owners could not be confirmed. <button type="button" className="underline" onClick={() => void records.refetch()}>Reload</button></p>}
    {records.data && <div className="space-y-3">
      {HANDOFF_DOMAINS.map((domain) => {
        const owner = records.data.ownership.find((row) => row.domain === domain.id);
        const stage = records.data.staging.find((row) => row.domain === domain.id);
        const state = handoffSnapshotState(stage, linkVersion, now);
        const sourceOwned = owner?.owner === 'mission_control';
        const paused = sourceOwned && (!staffEnabled || linkStatus !== 'verified' || owner.reviewed_link_version !== linkVersion);
        return <div key={domain.id} className="border-t border-border pt-3 space-y-1" data-testid={`handoff-${domain.id}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <h4 className="text-xs font-medium text-text">{domain.label}</h4>
            <p className="text-xs font-medium text-text">Owner: {sourceOwned ? 'Mission Control' : 'Portal'}{paused ? ' · Paused' : ''}</p>
          </div>
          {paused && <p className="text-xs text-warning">Source writing is paused. Portal writing stays excluded until an administrator explicitly restores Portal ownership.</p>}
          <p className="text-xs text-text-muted">{state.label}</p>
          {stage && <>
            <p className="break-all text-xs text-text-muted">Provider: {stage.provider_id.slice(0, 256)}</p>
            <p className="text-xs text-text-muted">Observed: {dateLabel(stage.observed_at)} · {Math.max(0, Math.floor((now - Date.parse(stage.observed_at)) / 60_000))} minutes ago</p>
            <p className="text-xs text-text-muted">{handoffSummary(domain.id, stage.payload).map((item) => `${item.label}: ${item.count ?? 'unknown'}`).join(' · ')}</p>
            <p className="break-all font-mono text-xs text-text-muted" title={stage.snapshot_hash.slice(0, 128)}>Snapshot: {stage.snapshot_hash.slice(0, 16)} · Mapping version {stage.link_version}</p>
          </>}
          {sourceOwned && <p className="text-xs text-text-muted">Last accepted source observation: {dateLabel(owner.last_accepted_observed_at)}</p>}
          {owner?.last_reason && <p className="break-words text-xs text-text-muted">Last review: {owner.last_reason.slice(0, 1000)}</p>}
          {canManage && <button type="button" disabled={save.isPending || records.isError || (!sourceOwned && (!state.ready || !staffEnabled || linkStatus !== 'verified'))}
            onClick={() => openReview(domain.id, sourceOwned ? 'portal' : 'mission_control')}
            className="text-xs text-primary disabled:opacity-40">{sourceOwned ? 'Review return to Portal' : 'Review transfer to Mission Control'}</button>}
        </div>;
      })}
    </div>}
    {canManage && review && <form onSubmit={(event) => { event.preventDefault(); save.mutate(); }} className="border-t border-border pt-3 space-y-3">
      <h4 className="text-sm font-medium text-text">{review.owner === 'portal' ? 'Restore Portal ownership' : 'Transfer to Mission Control'}: {HANDOFF_DOMAINS.find((domain) => domain.id === review.domain)?.label}</h4>
      {review.owner === 'mission_control' ? <>
        <p className="text-xs text-text-muted">Counts and a complete snapshot do not prove parity. Compare provider identity, sampled records, statuses, source observation times and report continuity with the current Portal records. Resolve or document differences before transferring. The server also checks fresh complete provider and location uptime coverage.</p>
        <label className="flex items-start gap-2 text-xs text-text">
          <input type="checkbox" checked={compared} disabled={save.isPending || reviewChanged} onChange={(event) => setCompared(event.target.checked)} />
          I reviewed record and report parity, resolved or documented differences, verified recovery, and approve updating the existing customer-visible network details for this domain.
        </label>
      </> : <p className="text-xs text-text-muted">This stops Mission Control writes for this domain and permits the Portal provider writer to resume. Existing records and history are retained. A fresh source snapshot is not required for recovery.</p>}
      {reviewChanged && <p role="alert" className="text-xs text-warning">The reviewed state changed. Close this review, reload and compare the current records again.</p>}
      <label className="block text-xs text-text">{review.owner === 'portal' ? 'Recovery reason' : 'Parity review and any accepted differences'}
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={5} maxLength={1000} required disabled={save.isPending || reviewChanged} rows={3} className="mt-1 block w-full rounded border border-border bg-surface p-2" />
      </label>
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={save.isPending || records.isError || reviewChanged || reason.trim().length < 5 || (review.owner === 'mission_control' && (!compared || !sourceReady))} className="rounded bg-primary px-3 py-2 text-xs text-white disabled:opacity-40">{save.isPending ? 'Saving…' : review.owner === 'portal' ? 'Confirm return to Portal' : 'Confirm source handoff'}</button>
        <button type="button" disabled={save.isPending} onClick={() => { setReview(null); save.reset(); }} className="text-xs text-text-muted">Cancel review</button>
      </div>
    </form>}
    {save.isError && <p role="alert" className="text-xs text-error">{handoffError(save.error)}</p>}
  </section>;
}
